import { z } from 'zod';

/** The sections the admin Settings screen renders (instructions/04_UI_UX.md
 * groups related toggles together rather than listing flat rows). */
export type SettingGroup = 'auth' | 'notifications' | 'features' | 'matching' | 'sla';

export const BOOL_SETTING_KEYS = [
  'auth.email_login',
  'auth.phone_otp',
  'notifications.push',
  'feature.chat',
  'feature.ratings',
  'feature.gps_checkin',
  'feature.compensation',
  'feature.khutbah_matching',
] as const;
export type BoolSettingKey = (typeof BOOL_SETTING_KEYS)[number];

export const NUMBER_SETTING_KEYS = [
  'match.weight_distance',
  'match.weight_availability',
  'match.weight_rating',
  'match.weight_reliability',
  'reliability.w_completion',
  'reliability.w_response',
  'reliability.w_checkin',
  'sla.response_hours_low',
  'sla.response_hours_normal',
  'sla.response_hours_high',
  'sla.response_hours_urgent',
] as const;
export type NumberSettingKey = (typeof NUMBER_SETTING_KEYS)[number];

export type SettingKey = BoolSettingKey | NumberSettingKey;

interface BoolSettingDef {
  readonly key: BoolSettingKey;
  readonly group: SettingGroup;
  readonly labelKey: string;
  readonly descriptionKey: string;
  readonly kind: 'bool';
  readonly default: boolean;
}

interface NumberSettingDef {
  readonly key: NumberSettingKey;
  readonly group: SettingGroup;
  readonly labelKey: string;
  readonly descriptionKey: string;
  readonly kind: 'number';
  readonly default: number;
  /** Client-side validation bounds. Defaults to [0, 1] (the matching/reliability
   * weights' natural range) when omitted — see `sla.*` keys below for an
   * example that overrides both. */
  readonly min?: number;
  readonly max?: number;
}

export type SettingDef = BoolSettingDef | NumberSettingDef;

/**
 * The client-side catalog of every runtime-config row: which UI group it
 * belongs to, its i18n keys, and the default to fall back to before the
 * `app_settings` query resolves (or if a key is ever missing server-side).
 * Defaults mirror the seed values in
 * supabase/migrations/20260722100000_phase10_app_settings.sql exactly.
 */
export const REGISTRY: readonly SettingDef[] = [
  {
    key: 'auth.email_login',
    group: 'auth',
    labelKey: 'config.auth.emailLogin.label',
    descriptionKey: 'config.auth.emailLogin.description',
    kind: 'bool',
    default: true,
  },
  {
    key: 'auth.phone_otp',
    group: 'auth',
    labelKey: 'config.auth.phoneOtp.label',
    descriptionKey: 'config.auth.phoneOtp.description',
    kind: 'bool',
    default: true,
  },
  {
    key: 'notifications.push',
    group: 'notifications',
    labelKey: 'config.notifications.push.label',
    descriptionKey: 'config.notifications.push.description',
    kind: 'bool',
    default: true,
  },
  {
    key: 'feature.chat',
    group: 'features',
    labelKey: 'config.features.chat.label',
    descriptionKey: 'config.features.chat.description',
    kind: 'bool',
    default: true,
  },
  {
    key: 'feature.ratings',
    group: 'features',
    labelKey: 'config.features.ratings.label',
    descriptionKey: 'config.features.ratings.description',
    kind: 'bool',
    default: true,
  },
  {
    key: 'feature.gps_checkin',
    group: 'features',
    labelKey: 'config.features.gpsCheckin.label',
    descriptionKey: 'config.features.gpsCheckin.description',
    kind: 'bool',
    default: true,
  },
  {
    key: 'feature.compensation',
    group: 'features',
    labelKey: 'config.features.compensation.label',
    descriptionKey: 'config.features.compensation.description',
    kind: 'bool',
    default: true,
  },
  {
    key: 'feature.khutbah_matching',
    group: 'features',
    labelKey: 'config.features.khutbahMatching.label',
    descriptionKey: 'config.features.khutbahMatching.description',
    kind: 'bool',
    default: true,
  },
  {
    key: 'match.weight_distance',
    group: 'matching',
    labelKey: 'config.matching.weightDistance.label',
    descriptionKey: 'config.matching.weightDistance.description',
    kind: 'number',
    default: 0.35,
  },
  {
    key: 'match.weight_availability',
    group: 'matching',
    labelKey: 'config.matching.weightAvailability.label',
    descriptionKey: 'config.matching.weightAvailability.description',
    kind: 'number',
    default: 0.35,
  },
  {
    key: 'match.weight_rating',
    group: 'matching',
    labelKey: 'config.matching.weightRating.label',
    descriptionKey: 'config.matching.weightRating.description',
    kind: 'number',
    default: 0.15,
  },
  {
    key: 'match.weight_reliability',
    group: 'matching',
    labelKey: 'config.matching.weightReliability.label',
    descriptionKey: 'config.matching.weightReliability.description',
    kind: 'number',
    default: 0.15,
  },
  {
    key: 'reliability.w_completion',
    group: 'matching',
    labelKey: 'config.matching.relCompletion.label',
    descriptionKey: 'config.matching.relCompletion.description',
    kind: 'number',
    default: 0.4,
  },
  {
    key: 'reliability.w_response',
    group: 'matching',
    labelKey: 'config.matching.relResponse.label',
    descriptionKey: 'config.matching.relResponse.description',
    kind: 'number',
    default: 0.3,
  },
  {
    key: 'reliability.w_checkin',
    group: 'matching',
    labelKey: 'config.matching.relCheckin.label',
    descriptionKey: 'config.matching.relCheckin.description',
    kind: 'number',
    default: 0.3,
  },
  {
    key: 'sla.response_hours_low',
    group: 'sla',
    labelKey: 'config.sla.responseHoursLow.label',
    descriptionKey: 'config.sla.responseHoursLow.description',
    kind: 'number',
    default: 72,
    min: 1,
    max: 500,
  },
  {
    key: 'sla.response_hours_normal',
    group: 'sla',
    labelKey: 'config.sla.responseHoursNormal.label',
    descriptionKey: 'config.sla.responseHoursNormal.description',
    kind: 'number',
    default: 24,
    min: 1,
    max: 500,
  },
  {
    key: 'sla.response_hours_high',
    group: 'sla',
    labelKey: 'config.sla.responseHoursHigh.label',
    descriptionKey: 'config.sla.responseHoursHigh.description',
    kind: 'number',
    default: 8,
    min: 1,
    max: 500,
  },
  {
    key: 'sla.response_hours_urgent',
    group: 'sla',
    labelKey: 'config.sla.responseHoursUrgent.label',
    descriptionKey: 'config.sla.responseHoursUrgent.description',
    kind: 'number',
    default: 2,
    min: 1,
    max: 500,
  },
];

/** The four match weights whose sum the admin screen warns about when != 1.0. */
export const MATCH_WEIGHT_KEYS: readonly NumberSettingKey[] = [
  'match.weight_distance',
  'match.weight_availability',
  'match.weight_rating',
  'match.weight_reliability',
];

function findDef(key: SettingKey): SettingDef {
  const def = REGISTRY.find((d) => d.key === key);
  if (!def) throw new Error(`Unknown app setting key: ${key}`);
  return def;
}

const rowSchema = z.object({
  key: z.string(),
  value: z.unknown(),
  kind: z.enum(['bool', 'number', 'string']),
  updated_at: z.string(),
});

export interface AppSetting {
  readonly key: string;
  readonly value: unknown;
  readonly kind: 'bool' | 'number' | 'string';
  readonly updatedAt: string;
}

export function mapAppSetting(row: unknown): AppSetting {
  const r = rowSchema.parse(row);
  return { key: r.key, value: r.value, kind: r.kind, updatedAt: r.updated_at };
}

/** A plain key -> raw jsonb value lookup, built once from the fetched rows. */
export type AppSettingsMap = Readonly<Record<string, unknown>>;

export function toSettingsMap(rows: readonly AppSetting[] | undefined): AppSettingsMap {
  const map: Record<string, unknown> = {};
  for (const row of rows ?? []) map[row.key] = row.value;
  return map;
}

/** Typed getter: current value for a bool key, falling back to the registry
 * default while the query is loading or if the row is ever missing. */
export function flag(map: AppSettingsMap | undefined, key: BoolSettingKey): boolean {
  const raw = map?.[key];
  if (typeof raw === 'boolean') return raw;
  return (findDef(key) as BoolSettingDef).default;
}

/** Typed getter: current value for a numeric key, same fallback behavior. */
export function num(map: AppSettingsMap | undefined, key: NumberSettingKey): number {
  const raw = map?.[key];
  if (typeof raw === 'number') return raw;
  return (findDef(key) as NumberSettingDef).default;
}
